import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import AuthCheckLoading from "./components/AuthCheckLoading";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { installResumeCheck } from "./lib/resumeCheck";
import { retirePwa } from "./lib/retirePwa";
import { queryRetryDelay, shouldRetryQuery } from "./lib/queryRetry";
import { useAppStore } from "./hooks/useAppStore";
import "./i18n/index";
import "./styles/global.css";

function NoClerkSessionProvider({ children }: React.PropsWithChildren) {
  return <>{children}</>;
}

const ClerkSessionProvider = React.lazy<React.ComponentType<React.PropsWithChildren>>(() => {
  if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim()) {
    return Promise.resolve({ default: NoClerkSessionProvider });
  }
  return import("./components/ClerkSessionProvider");
});

function AuthLoadingFallback() {
  return <AuthCheckLoading />;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Cancel stale work synchronously when the herd changes, while retaining its
// cache for a quick return. This module owns the QueryClient, avoiding a store
// → QueryClient import cycle.
useAppStore.subscribe((state, previousState) => {
  const oldHerdId = previousState.selectedHerdId;
  if (!oldHerdId || oldHerdId === state.selectedHerdId) return;
  void queryClient.cancelQueries({
    predicate: (query) => query.queryKey.some((part) => part === oldHerdId),
  });
});

// PWA-Aufwach-Prüfung: nach langer Hintergrundzeit Session/Version prüfen,
// damit die App nie eingefroren zurückkommt (siehe lib/resumeCheck.ts).
installResumeCheck();
void retirePwa().catch((error) => {
  console.warn("Alta PWA: Abschaltung beim Laden fehlgeschlagen", error);
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
          <React.Suspense fallback={<AuthLoadingFallback />}>
            <ClerkSessionProvider>
              <App />
            </ClerkSessionProvider>
          </React.Suspense>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
